import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import * as RMenu from '@radix-ui/react-dropdown-menu';
import { UserCheck, CheckCircle2, RotateCcw, StickyNote, Eye, PenLine, ChevronDown, Trash2, Lock } from 'lucide-react';
import api from '../../utils/api';
import { errMsg } from '../SupportCoordination/kit';
import { avatarColor, initials, listTime } from './mailUi';

// Team tools on a conversation in a shared mailbox: who has it (and whether it is done), internal
// notes the sender never sees, and who else is looking at it or writing a reply right now.
const HEARTBEAT_MS = 20000;
const full = (p) => `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Someone';

export function usePresence(messageId, state, enabled) {
  const [others, setOthers] = useState([]);
  useEffect(() => {
    if (!enabled || !messageId) return undefined;
    let alive = true;
    const beat = () => api.post(`/mail/messages/${messageId}/presence`, { state }).then((r) => { if (alive) setOthers(r.data || []); }).catch(() => {});
    beat();
    const t = setInterval(beat, state === 'replying' ? 15000 : HEARTBEAT_MS);
    return () => {
      alive = false; clearInterval(t);
      // Leaving a reply goes back to just looking; leaving the message says so.
      api.post(`/mail/messages/${messageId}/presence`, { state: state === 'replying' ? 'viewing' : 'left' }).catch(() => {});
    };
  }, [messageId, state, enabled]);
  return others;
}

export function PresenceNote({ others, replying }) {
  const writing = (others || []).filter((p) => p.state === 'replying');
  const looking = (others || []).filter((p) => p.state !== 'replying');
  if (!writing.length && !looking.length) return null;
  const names = (xs) => xs.map((p) => p.name || 'Someone').join(', ');
  return (
    <div className={clsx('flex items-center gap-2 rounded-lg px-3 py-2 text-[12.5px]', writing.length ? 'bg-amber-50 ring-1 ring-amber-200 text-amber-900' : 'bg-slate-50 ring-1 ring-slate-200 text-slate-700')} role="status">
      {writing.length ? <PenLine size={14} className="flex-shrink-0" aria-hidden /> : <Eye size={14} className="flex-shrink-0" aria-hidden />}
      <span>
        {writing.length ? <><b>{names(writing)}</b> {writing.length === 1 ? 'is' : 'are'} writing a reply to this{replying ? ' too. Check before you send, so it is not answered twice.' : '.'}</> : <><b>{names(looking)}</b> {looking.length === 1 ? 'is' : 'are'} looking at this.</>}
      </span>
    </div>
  );
}

export default function TeamPanel({ messageId, onChanged }) {
  const [team, setTeam] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const others = usePresence(messageId, 'viewing', !!team);
  const load = useCallback(() => api.get(`/mail/messages/${messageId}/team`).then((r) => setTeam(r.data)).catch(() => setTeam(null)), [messageId]);
  useEffect(() => { setTeam(null); setNotesOpen(false); load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  const notesRef = useRef(null);
  if (!team) return null;
  const a = team.assignment;
  const done = a && a.status === 'done';
  const act = async (body, msg) => {
    try { const r = await api.post(`/mail/messages/${messageId}/assign`, body); setTeam(r.data); if (msg) toast.success(msg); onChanged && onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    setBusy(true);
    try { const r = await api.post(`/mail/messages/${messageId}/comments`, { body: note }); setTeam(r.data); setNote(''); onChanged && onChanged(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const removeNote = async (c) => {
    if (!window.confirm('Remove this note?')) return;
    try { await api.delete(`/mail/comments/${c.id}`); load(); onChanged && onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const count = (team.comments || []).length;
  return (
    <div className="mt-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <RMenu.Root>
          <RMenu.Trigger asChild>
            <button type="button" className={clsx('inline-flex items-center gap-1.5 h-8 pl-2 pr-2 rounded-lg ring-1 text-[12.5px] font-semibold data-[state=open]:bg-slate-100',
              a && a.assignee_id ? 'ring-sky-200 bg-sky-50 text-sky-900' : 'ring-slate-200 text-slate-700 hover:bg-slate-50')}>
              {a && a.assignee_id
                ? <span className="w-5 h-5 rounded-full text-white text-[9px] font-bold flex items-center justify-center" style={{ background: avatarColor(a.assignee_name) }} aria-hidden>{initials(a.assignee_name)}</span>
                : <UserCheck size={14} className="text-slate-500" aria-hidden />}
              {a && a.assignee_id ? a.assignee_name : 'Not assigned'}
              <ChevronDown size={12} className="opacity-60" aria-hidden />
            </button>
          </RMenu.Trigger>
          <RMenu.Portal>
            <RMenu.Content align="start" sideOffset={4} className="z-[80] min-w-[220px] max-h-[320px] overflow-y-auto mail-scroll bg-white rounded-lg shadow-lg ring-1 ring-slate-200 p-1">
              <RMenu.Label className="px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Give this conversation to</RMenu.Label>
              <RMenu.Item onSelect={() => act({ assignee_id: 'me', status: 'open' }, 'It is yours')} className="flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px] text-slate-800 cursor-pointer outline-none data-[highlighted]:bg-slate-100 font-semibold">Me</RMenu.Item>
              {(team.people || []).map((p) => (
                <RMenu.Item key={p.id} onSelect={() => act({ assignee_id: p.id, status: 'open' }, `Given to ${full(p)}`)} className="flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px] text-slate-700 cursor-pointer outline-none data-[highlighted]:bg-slate-100">
                  <span className="w-5 h-5 rounded-full text-white text-[9px] font-bold flex items-center justify-center" style={{ background: avatarColor(full(p)) }} aria-hidden>{initials(full(p))}</span>
                  <span className="flex-1">{full(p)}</span>
                </RMenu.Item>
              ))}
              {a && a.assignee_id && <><RMenu.Separator className="my-1 h-px bg-slate-100" /><RMenu.Item onSelect={() => act({ assignee_id: null })} className="px-2.5 py-2 rounded-md text-[13px] text-slate-700 cursor-pointer outline-none data-[highlighted]:bg-slate-100">Nobody</RMenu.Item></>}
            </RMenu.Content>
          </RMenu.Portal>
        </RMenu.Root>
        {done
          ? <button type="button" onClick={() => act({ status: 'open' }, 'Open again')} className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg ring-1 ring-emerald-200 bg-emerald-50 text-emerald-800 text-[12.5px] font-semibold"><CheckCircle2 size={14} aria-hidden />Done<span className="font-normal text-emerald-700 inline-flex items-center gap-1"> · <RotateCcw size={12} aria-hidden />Reopen</span></button>
          : <button type="button" onClick={() => act({ status: 'done' }, 'Marked done')} className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg ring-1 ring-slate-200 text-slate-700 text-[12.5px] font-semibold hover:bg-slate-50"><CheckCircle2 size={14} className="text-emerald-600" aria-hidden />Mark done</button>}
        <button type="button" onClick={() => { setNotesOpen((v) => !v); setTimeout(() => notesRef.current && notesRef.current.focus(), 50); }} aria-expanded={notesOpen}
          className={clsx('inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg ring-1 text-[12.5px] font-semibold', count ? 'ring-violet-200 bg-violet-50 text-violet-800' : 'ring-slate-200 text-slate-700 hover:bg-slate-50')}>
          <StickyNote size={14} aria-hidden />Internal notes{count ? ` (${count})` : ''}
        </button>
      </div>
      <PresenceNote others={others} />
      {(notesOpen || count > 0) && (
        <div className="rounded-xl ring-1 ring-violet-200 bg-violet-50/40 p-3 space-y-2">
          <p className="flex items-center gap-1.5 text-[11.5px] text-violet-900"><Lock size={11} aria-hidden />Only your team sees these notes. They are never sent and never go to the mailbox.</p>
          {(team.comments || []).map((c) => (
            <div key={c.id} className="group flex gap-2.5">
              <span className="w-7 h-7 rounded-full text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0" style={{ background: avatarColor(c.author_name) }} aria-hidden>{initials(c.author_name)}</span>
              <div className="min-w-0 flex-1 rounded-lg bg-white ring-1 ring-violet-100 px-3 py-2">
                <p className="text-[12px] text-slate-500"><b className="text-slate-800">{c.author_name || 'Someone'}</b> · {listTime(c.created_at)}</p>
                <p className="text-[13px] text-slate-800 whitespace-pre-wrap break-words">{c.body}</p>
              </div>
              {c.mine && <button type="button" onClick={() => removeNote(c)} aria-label="Remove this note" className="opacity-0 group-hover:opacity-100 focus:opacity-100 w-7 h-7 rounded-md flex items-center justify-center text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={13} aria-hidden /></button>}
            </div>
          ))}
          <form onSubmit={addNote} className="flex gap-2">
            <textarea ref={notesRef} value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add a note for your team" aria-label="Internal note"
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) addNote(e); }}
              className="flex-1 min-w-0 rounded-lg ring-1 ring-inset ring-violet-200 bg-white px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-violet-400" />
            <button type="submit" disabled={busy || !note.trim()} className="self-end h-9 px-3 rounded-lg bg-violet-600 text-white text-[12.5px] font-semibold disabled:opacity-50">Add</button>
          </form>
        </div>
      )}
    </div>
  );
}
